/**
 * Fetches the analyzer's preview SVG and extracts per-loop and
 * per-component bboxes / centroids in SVG user-space (the 800×600
 * viewBox; see `lib/dxfTransform.ts` for the mm↔SVG mapping).
 *
 * Used by overlays (warning callouts, future measurement snap targets)
 * to anchor annotations to specific geometry without coordinating
 * coordinate state with the backend.
 *
 * The analyzer's SVG only uses `M`/`L` path commands (see
 * `_points_to_path_d` in `packages/analyzer/src/dxf_analyzer/svg.py`),
 * so a tiny regex parser is enough — pulling in a full SVG path lib
 * would be wasteful.
 *
 * Results are memoised by URL; one fetch per session per preview.
 */

import { useEffect, useState } from "react";

export type LoopRole = "outer" | "inner" | "hole" | "open" | "main";

export interface DxfShape {
  /** Centroid of the shape in SVG user-space (viewBox 800×600). */
  centroid: { x: number; y: number };
  /** Axis-aligned bounding box in SVG user-space. */
  bbox: { x: number; y: number; w: number; h: number };
  /** Path role from the analyzer (data-role attribute). */
  role: LoopRole;
}

export interface ParsedDxfPreview {
  /** SVG viewBox dimensions, normally 800×600. */
  viewBox: { w: number; h: number };
  /** Loops keyed by `data-loop-id`. */
  loops: Map<string, DxfShape>;
  /** Components keyed by `data-component-id` (open components only — closed components map to loops). */
  components: Map<string, DxfShape>;
}

const cache = new Map<string, Promise<ParsedDxfPreview | null>>();

export function getParsedDxfPreview(url: string): Promise<ParsedDxfPreview | null> {
  if (!url) return Promise.resolve(null);
  let promise = cache.get(url);
  if (!promise) {
    promise = fetchAndParse(url).catch(() => null);
    cache.set(url, promise);
  }
  return promise;
}

/**
 * React hook that resolves the parsed preview for the given URL.
 * Returns ``null`` until the fetch completes (or on failure). Cached
 * across components so multiple consumers fetch once.
 */
export function useParsedDxfPreview(url: string | undefined | null): ParsedDxfPreview | null {
  const [parsed, setParsed] = useState<ParsedDxfPreview | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!url) {
      setParsed(null);
      return;
    }
    getParsedDxfPreview(url).then((p) => {
      if (!cancelled) setParsed(p);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return parsed;
}

async function fetchAndParse(url: string): Promise<ParsedDxfPreview | null> {
  const res = await fetch(url, { credentials: "omit" });
  if (!res.ok) return null;
  const text = await res.text();
  const doc = new DOMParser().parseFromString(text, "image/svg+xml");
  const svg = doc.documentElement;
  if (!svg || svg.tagName.toLowerCase() !== "svg") return null;

  const viewBoxAttr = svg.getAttribute("viewBox") ?? "0 0 800 600";
  const [, , vw, vh] = viewBoxAttr.split(/\s+/).map(Number);

  const loops = new Map<string, DxfShape>();
  const components = new Map<string, DxfShape>();

  const paths = svg.querySelectorAll("path[d]");
  paths.forEach((path) => {
    const d = path.getAttribute("d") ?? "";
    const points = parseMlPath(d);
    if (points.length === 0) return;
    const role = (path.getAttribute("data-role") ?? "outer") as LoopRole;
    const shape: DxfShape = {
      centroid: centroid(points),
      bbox: bbox(points),
      role,
    };
    const loopId = path.getAttribute("data-loop-id");
    if (loopId) loops.set(loopId, shape);
    const componentId = path.getAttribute("data-component-id");
    if (componentId) components.set(componentId, shape);
  });

  return {
    viewBox: { w: vw || 800, h: vh || 600 },
    loops,
    components,
  };
}

const ML_RE = /[ML]\s*([\d.+-eE]+)\s*,\s*([\d.+-eE]+)/g;

function parseMlPath(d: string): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (const match of d.matchAll(ML_RE)) {
    const x = Number(match[1]);
    const y = Number(match[2]);
    if (Number.isFinite(x) && Number.isFinite(y)) out.push({ x, y });
  }
  return out;
}

function centroid(points: { x: number; y: number }[]): { x: number; y: number } {
  let sx = 0;
  let sy = 0;
  for (const p of points) {
    sx += p.x;
    sy += p.y;
  }
  return { x: sx / points.length, y: sy / points.length };
}

function bbox(points: { x: number; y: number }[]): { x: number; y: number; w: number; h: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
